/**
 * ExplainerCard — modular knowledge card for site-visit mode.
 *
 * Three tap states:
 *   👁️ Viewed     — opened during conversation
 *   ⭐ Starred    — relevant to this design
 *   📄 In report  — include in PDF leave-behind
 *
 * The card is "pulled, not pushed" — the engineer opens it when the
 * customer asks why, then taps ⭐ or 📄 without interrupting the flow.
 */

import { useState } from 'react';
import type { Explainer, ExplainerState } from './explainerRegistry';
import { renderExplainerVisual } from './explainerVisualRegistry';

const STATE_ICONS: Record<ExplainerState, string> = {
  unseen: '',
  viewed: '👁️',
  starred: '⭐',
  'in-report': '📄',
};

const STATE_LABELS: Record<ExplainerState, string> = {
  unseen: 'Not viewed',
  viewed: 'Viewed',
  starred: 'Relevant',
  'in-report': 'In report',
};

interface Props {
  explainer: Explainer;
  onView: (id: string) => void;
  onStar: (id: string) => void;
  onReport: (id: string) => void;
}

export function ExplainerCard({ explainer, onView, onStar, onReport }: Props) {
  const [expanded, setExpanded] = useState(false);

  const handleExpand = () => {
    if (!expanded) {
      onView(explainer.id);
    }
    setExpanded(!expanded);
  };

  const hasPrerequisites = explainer.prerequisites.length > 0;

  return (
    <div className={`explainer-card explainer-card--${explainer.state}`}>
      <div className="explainer-card__header" onClick={handleExpand} role="button" tabIndex={0}>
        <div className="explainer-card__title-row">
          <span className="explainer-card__topic">{explainer.topic}</span>
          <h4 className="explainer-card__title">{explainer.title}</h4>
        </div>
        <div className="explainer-card__state-row">
          {explainer.state !== 'unseen' && (
            <span className="explainer-card__state-badge">
              {STATE_ICONS[explainer.state]} {STATE_LABELS[explainer.state]}
            </span>
          )}
          <span className="explainer-card__expand-icon">{expanded ? '−' : '+'}</span>
        </div>
      </div>

      <p className="explainer-card__desc">{explainer.description}</p>

      {expanded && (
        <div className="explainer-card__body">
          {hasPrerequisites && (
            <div className="hub-note">
              Builds on: {explainer.prerequisites.join(', ')}
            </div>
          )}

          {explainer.content.map((block, i) => (
            <div key={i} className="explainer-card__content-block">
              {block.type === 'text' && (
                <div
                  className="explainer-card__text"
                  dangerouslySetInnerHTML={{
                    __html: (block.body ?? '')
                      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                      .replace(/\*(.*?)\*/g, '<em>$1</em>'),
                  }}
                />
              )}
              {(block.type === 'diagram' || block.type === 'chart' || block.type === 'animation' || block.type === 'comparison') && (() => {
                const visual = renderExplainerVisual(block.componentKey);

                if (visual) {
                  return (
                    <div className="explainer-card__visual">
                      <div className="explainer-card__visual-content">{visual}</div>
                      {block.caption && <span className="explainer-card__caption">{block.caption}</span>}
                    </div>
                  );
                }

                return (
                  <div className="explainer-card__visual-placeholder">
                    <span className="explainer-card__component-key">[{block.componentKey}]</span>
                    {block.caption && <span className="explainer-card__caption">{block.caption}</span>}
                  </div>
                );
              })()}
            </div>
          ))}

          {/* Action buttons — tap without interrupting conversation */}
          <div className="explainer-card__actions">
            <button
              className={`explainer-card__action-btn${explainer.state === 'starred' ? ' explainer-card__action-btn--active' : ''}`}
              onClick={e => { e.stopPropagation(); onStar(explainer.id); }}
              title="Mark as relevant to this design"
            >
              ⭐ Relevant
            </button>
            <button
              className={`explainer-card__action-btn${explainer.state === 'in-report' ? ' explainer-card__action-btn--active' : ''}`}
              onClick={e => { e.stopPropagation(); onReport(explainer.id); }}
              title="Include in PDF report"
            >
              📄 In report
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
